import { useEffect, useState } from 'react';

/**
 * How far a bottom-anchored floating control must sit above whatever else owns the
 * bottom edge (cookie banner, per-response nav bar). Elements that park there carry
 * `data-bottom-overlay`. A fixed offset was not enough: on a phone the consent banner
 * is three lines tall and buried the support button until it was dismissed.
 */
export const useBottomOverlayInset = (base = 16): number => {
    const [inset, setInset] = useState(base);

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | null = null;
        const measure = () => {
            timer = null;
            let tallest = 0;
            document.querySelectorAll('[data-bottom-overlay]').forEach((element) => {
                const rect = element.getBoundingClientRect();
                // Only count overlays actually parked at the bottom of the viewport.
                if (rect.height > tallest && rect.bottom >= window.innerHeight - 2) {
                    tallest = rect.height;
                }
            });
            setInset(Math.round(tallest) + base);
        };
        // Debounced with a timer, not requestAnimationFrame: rAF is paused in a
        // background tab, which left the button stranded above a banner that had
        // already been dismissed.
        const schedule = () => {
            if (!timer) {
                timer = setTimeout(measure, 100);
            }
        };
        measure();
        const observer = new MutationObserver(schedule);
        observer.observe(document.body, { childList: true, subtree: true });
        window.addEventListener('resize', schedule);
        return () => {
            if (timer) {
                clearTimeout(timer);
            }
            observer.disconnect();
            window.removeEventListener('resize', schedule);
        };
    }, [base]);

    return inset;
};
